import {
  Modal,
  Table,
  Spin,
  message,
  Tag,
  Button,
  Space,
  Typography,
  Divider,
  InputNumber,
  FloatButton,
  Descriptions,
  Badge,
} from "antd";
import { useRef, useState, useMemo, useCallback, useEffect } from "react";
import dayjs from "dayjs";
import axios from "axios";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import {
  HEADERS_EXPORT_BEN,
  POST_EIDT_DTR_PAYMENT,
  POST_SYNC_DTR_PAYMENT,
} from "../../../API/apiUrls";
import { purple } from "@mui/material/colors";

const { Text } = Typography;

const fmt = (n) =>
  Number(n ?? 0).toLocaleString("th-TH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const ListDataPayment = ({
  open,
  close,
  dataLoad,
  onSaveSuccess,
  onApproveSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [loadingSave, setLoadingSave] = useState(false);
  const scrollRef = useRef(null);

  const originalItems = useRef((dataLoad?.items || []).map((i) => ({ ...i })));

  const [editItems, setEditItems] = useState(() =>
    (dataLoad?.items || []).map((i) => ({ ...i })),
  );

  useEffect(() => {
    const items = (dataLoad?.items || []).map((i) => ({ ...i }));
    originalItems.current = items.map((i) => ({ ...i }));
    setEditItems(items);
  }, [dataLoad]);

  const groupedByInv = useMemo(() => {
    const map = {};
    editItems.forEach((item) => {
      if (!map[item.invId]) map[item.invId] = [];
      map[item.invId].push(item);
    });
    return Object.values(map);
  }, [editItems]);

  const grandTotal = useMemo(() => {
    return editItems.reduce(
      (sum, i) =>
        sum +
        Number(i.tr14 ?? 0) +
        Number(i.familyRegistration ?? 0) +
        Number(i.operationFee ?? 0) +
        Number(i.spouseFee ?? 0) +
        Number(i.additionalFee ?? 0),
      0,
    );
  }, [editItems]);

  const ducumentTotal = useMemo(() => {
    return editItems.reduce(
      (sum, i) =>
        sum +
        Number(i.tr14 ?? 0) +
        Number(i.familyRegistration ?? 0) +
        Number(i.spouseFee ?? 0),
      0,
    );
  }, [editItems]);

  const operationTotal = useMemo(() => {
    return editItems.reduce(
      (sum, i) =>
        sum + Number(i.operationFee ?? 0) + Number(i.additionalFee ?? 0),
      0,
    );
  }, [editItems]);

  const withheldTotal = useMemo(() => {
    return editItems.reduce((sum, i) => sum + Number(i.operationFee ?? 0), 0);
  }, [editItems]);

  const changedCount = useMemo(() => {
    return editItems.filter((item) => {
      const orig = originalItems.current.find(
        (o) => o.moneyOnlineId === item.moneyOnlineId,
      );
      if (!orig) return false;
      return (
        orig.tr14 !== item.tr14 ||
        orig.familyRegistration !== item.familyRegistration ||
        orig.operationFee !== item.operationFee ||
        orig.spouseFee !== item.spouseFee ||
        orig.additionalFee !== item.additionalFee
      );
    }).length;
  }, [editItems]);

  const handleCancel = () => close();

  const updateItem = useCallback((moneyOnlineId, field, value) => {
    setEditItems((prev) =>
      prev.map((item) =>
        item.moneyOnlineId === moneyOnlineId
          ? { ...item, [field]: String(value ?? 0) }
          : item,
      ),
    );
  }, []);

  const recalcTotal = (item) => {
    const total =
      Number(item.tr14) +
      Number(item.familyRegistration) +
      Number(item.operationFee) +
      Number(item.spouseFee) +
      Number(item.additionalFee);
    return { ...item, total: String(total) };
  };

  const getChangedItems = useCallback(() => {
    return editItems.filter((item) => {
      const orig = originalItems.current.find(
        (o) => o.moneyOnlineId === item.moneyOnlineId,
      );
      if (!orig) return false;
      return (
        orig.tr14 !== item.tr14 ||
        orig.familyRegistration !== item.familyRegistration ||
        orig.operationFee !== item.operationFee ||
        orig.spouseFee !== item.spouseFee ||
        orig.additionalFee !== item.additionalFee
      );
    });
  }, [editItems]);

  const handleSave = async () => {
    const changedItems = getChangedItems();
    if (changedItems.length === 0) {
      message.warning("ไม่มีการเปลี่ยนแปลงข้อมูล");
      return;
    }

    setLoadingSave(true);
    try {
      // หา dtrPaymentId ที่มีการเปลี่ยนแปลง
      const changedDtrIds = new Set(changedItems.map((i) => i.dtrPaymentId));

      // ดึงทุก item ของ dtrPaymentId นั้นจาก data ทั้งหมด (ไม่ใช่แค่ changedItems)
      const groupByDtr = {};
      changedDtrIds.forEach((dtrId) => {
        groupByDtr[dtrId] = editItems
          .filter((item) => item.dtrPaymentId === dtrId)
          .map((item) => recalcTotal(item));
      });

      console.log("groupByDtr", groupByDtr);

      const results = await Promise.all(
        Object.values(groupByDtr).map((items) =>
          axios.post(POST_EIDT_DTR_PAYMENT, items, {
            headers: HEADERS_EXPORT_BEN,
          }),
        ),
      );

      const hasFailed = results.some((r) => r.status !== 200);
      if (hasFailed) {
        message.error("อัปเดตบางรายการไม่สำเร็จ");
        return;
      }

      message.success(
        `บันทึก ${changedItems.length} รายการสำเร็จ กำลังโหลดข้อมูลใหม่...`,
      );

      await onSaveSuccess();
    } catch (error) {
      message.error(error.response?.data?.message || "บันทึกไม่สำเร็จ");
    } finally {
      setLoadingSave(false);
    }
  };

  const buildAPOTHTRN = (items) => {
    const map = {};
    items.forEach((item) => {
      if (!map[item.invId]) map[item.invId] = [];
      map[item.invId].push(item);
    });

    const result = [];

    Object.values(map).forEach((group) => {
      const first = group[0];
      const contno = first.CONTNO;
      const invId = first.invId;
      const invRoundId = first.invRoundId;

      const amt5360_01 = group.reduce(
        (sum, r) =>
          sum +
          Number(r.tr14 ?? 0) +
          Number(r.spouseFee ?? 0) +
          Number(r.familyRegistration ?? 0),
        0,
      );

      const amt5211_02 = group.reduce(
        (sum, r) =>
          sum + Number(r.operationFee ?? 0) + Number(r.additionalFee ?? 0),
        0,
      );

      if (amt5360_01 > 0) {
        result.push({
          EXPENSECOD: "5360-11",
          DESC: `ค่าธรรมเนียมอื่นๆ/${contno}`,
          AMOUNT: amt5360_01,
          invRoundId,
          invId,
          contno,
        });
      }

      if (amt5211_02 > 0) {
        result.push({
          EXPENSECOD: "5211-02",
          DESC: `ค่าติดตามส่วนกฎหมาย/${contno}`,
          AMOUNT: amt5211_02,
          invRoundId,
          invId,
          contno,
        });
      }
    });

    return result;
  };
  const getPrefix = (contno = "") => {
    const dashIdx = contno.indexOf("-");

    return dashIdx > 0
      ? contno.slice(0, dashIdx).toLowerCase()
      : contno.slice(0, 2).toLowerCase();
  };

  const getAccountLabel = (items) => {
    const prefixes = new Set(items.map((i) => getPrefix(i.CONTNO)));

    const labels = [];

    // ── กลุ่ม V/8 : V-, 8-, 12, 5-, 7-, 6- ─────────────────────
    const GROUP_V8 = ["8", "12", "5", "7", "6"]; // ไม่รวม v
    const hasV = prefixes.has("v");
    const hasV8Rest = GROUP_V8.some((p) => prefixes.has(p));
    if (hasV && hasV8Rest) labels.push("1,8");
    else if (hasV8Rest) labels.push("8");
    else if (hasV) labels.push("v");

    // ── กลุ่ม 1/3 : 1-, 3- ──────────────────────────────────────
    const has1 = prefixes.has("1");
    const has3 = prefixes.has("3");
    const hasN = prefixes.has("N");

    if (has1 && has3 && hasN) labels.push("1,3,N");
    else if (has3) labels.push("3");
    else if (has1) labels.push("1");
    else if (hasN) labels.push("N");

    // ── กลุ่ม 2 : 2- ────────────────────────────────────────────
    if (prefixes.has("2")) labels.push("2");

    return labels;
  };

  const preDataApprove = () => {
    const recalcedItems = editItems.map(recalcTotal);
    console.log("recalcedItems", recalcedItems);
    const checkContno = getAccountLabel(recalcedItems);

    const approvePayload = [
      {
        TEMPBILL: dataLoad.TEMPBILL,
        NETPAY: grandTotal,
        MEMO1: `ค่าสืบทรัพย์สัญญา ${checkContno}/นายธนกฤต มานนท์`,
        APOTHTRN: buildAPOTHTRN(recalcedItems),
      },
    ];

    console.log("approvePayload", approvePayload);
    handleApprove(approvePayload);
  };

  const handleApprove = async (approvePayload) => {
    setLoading(true);
    try {
      const res = await axios.post(POST_SYNC_DTR_PAYMENT, approvePayload, {
        headers: HEADERS_EXPORT_BEN,
      });

      if (res.status === 200) {
        message.success("ตั้งบิลเบิกสำเร็จ");
        await onApproveSuccess();
      }
    } catch (error) {
      message.error(error.response?.data?.message || "ตั้งบิลไม่สำเร็จ");
    } finally {
      setLoading(false);
    }
  };

  const personColumns = [
    {
      title: "ประเภท",
      key: "garno",
      align: "center",
      width: 90,
      render: (_, r) => (
        <Tag
          color={
            r.garno === 0 && !r.relationCustomerId
              ? "blue"
              : r.garno > 0
                ? "orange"
                : "purple"
          }
        >
          {r.garno === 0 && !r.relationCustomerId
            ? "ลูกหนี้"
            : r.garno > 0
              ? `ผู้ค้ำ ${r.garno}`
              : r.relation}
        </Tag>
      ),
    },
    { title: "รหัสบัตร", dataIndex: "CUSCOD", align: "center", width: 140 },
    {
      title: "ชื่อ-นามสกุล",
      key: "name",
      align: "center",
      render: (_, r) => `${r.SNAM}${r.NAME1} ${r.NAME2}`,
    },
    {
      title: "tr14",
      key: "tr14",
      align: "right",
      width: 90,
      render: (_, r) => (
        <InputNumber
          size="small"
          min={0}
          value={Number(r.tr14)}
          onChange={(v) => updateItem(r.moneyOnlineId, "tr14", v)}
          style={{ width: 80 }}
        />
      ),
    },
    {
      title: "ทะเบียนบ้าน",
      key: "familyRegistration",
      align: "right",
      width: 110,
      render: (_, r) => (
        <InputNumber
          size="small"
          min={0}
          value={Number(r.familyRegistration)}
          onChange={(v) => updateItem(r.moneyOnlineId, "familyRegistration", v)}
          style={{ width: 80 }}
        />
      ),
    },
    {
      title: "ค่าดำเนินการ",
      key: "operationFee",
      align: "right",
      width: 110,
      render: (_, r) => (
        <InputNumber
          size="small"
          min={0}
          value={Number(r.operationFee)}
          onChange={(v) => updateItem(r.moneyOnlineId, "operationFee", v)}
          style={{ width: 80 }}
        />
      ),
    },
    {
      title: "คู่สมรส",
      key: "spouseFee",
      align: "right",
      width: 90,
      render: (_, r) => (
        <InputNumber
          size="small"
          min={0}
          value={Number(r.spouseFee)}
          onChange={(v) => updateItem(r.moneyOnlineId, "spouseFee", v)}
          style={{ width: 80 }}
        />
      ),
    },
    {
      title: "เพิ่มเติม",
      key: "additionalFee",
      align: "right",
      width: 90,
      render: (_, r) => (
        <InputNumber
          size="small"
          min={0}
          value={Number(r.additionalFee)}
          onChange={(v) => updateItem(r.moneyOnlineId, "additionalFee", v)}
          style={{ width: 80 }}
        />
      ),
    },
    {
      title: "รวม/คน (บาท)",
      key: "total",
      align: "right",
      width: 120,
      render: (_, r) => {
        const total =
          Number(r.tr14) +
          Number(r.familyRegistration) +
          Number(r.operationFee) +
          Number(r.spouseFee) +
          Number(r.additionalFee);
        const orig = originalItems.current.find(
          (o) => o.moneyOnlineId === r.moneyOnlineId,
        );
        const isChanged =
          orig &&
          (orig.tr14 !== r.tr14 ||
            orig.familyRegistration !== r.familyRegistration ||
            orig.operationFee !== r.operationFee ||
            orig.spouseFee !== r.spouseFee ||
            orig.additionalFee !== r.additionalFee);
        return (
          <Text type={isChanged ? "warning" : "danger"} strong>
            {total.toLocaleString("th-TH")}
            {isChanged && " ✱"}
          </Text>
        );
      },
    },
  ];

  const mainColumns = [
    {
      title: "ลำดับ",
      key: "idx",
      align: "center",
      width: 60,
      render: (_, __, i) => i + 1,
    },
    {
      title: "เลขสัญญา",
      key: "CONTNO",
      align: "center",
      width: 140,
      render: (_, rows) => <Text strong>{rows[0].CONTNO}</Text>,
    },
    {
      title: "ชื่อลูกหนี้",
      key: "CUSTNAME",
      align: "center",
      render: (_, rows) => rows[0].CUSTNAME,
    },
    {
      title: "จำนวนบุคคล",
      key: "count",
      align: "center",
      width: 100,
      render: (_, rows) => `${rows.length} คน`,
    },
    {
      title: "ค่าคัดเอกสาร",
      key: "docFee",
      align: "right",
      width: 130,
      render: (_, rows) => {
        const total = rows.reduce(
          (sum, r) =>
            sum +
            Number(r.familyRegistration ?? 0) +
            Number(r.tr14 ?? 0) +
            Number(r.spouseFee ?? 0),
          0,
        );
        return <Text strong>{total.toLocaleString("th-TH")}</Text>;
      },
    },
    {
      title: "ค่าดำเนินการ",
      key: "opFee",
      align: "right",
      width: 130,
      render: (_, rows) => {
        const total = rows.reduce(
          (sum, r) =>
            sum + Number(r.operationFee ?? 0) + Number(r.additionalFee ?? 0),
          0,
        );
        return <Text strong>{total.toLocaleString("th-TH")}</Text>;
      },
    },
    {
      title: "ยอดรวม (บาท)",
      key: "NETAMT",
      align: "right",
      width: 130,
      render: (_, rows) => (
        <Text type="danger" strong>
          {Number(rows[0].NETAMT).toLocaleString()}
        </Text>
      ),
    },
  ];

  const isBusy = loading || loadingSave;

  return (
    <Spin spinning={isBusy} tip="กำลังดำเนินการ...">
      <Modal
        title={
          <Space>
            <Text strong style={{ fontSize: 16 }}>
              ✏️ รายละเอียดบิล
            </Text>
            <Tag color="blue" style={{ fontSize: 13 }}>
              {dataLoad?.TEMPBILL}
            </Tag>
            {changedCount > 0 && (
              <Badge count={`แก้ไข ${changedCount} รายการ`} color="orange" />
            )}
          </Space>
        }
        open={open}
        onCancel={handleCancel}
        width={1250}
        footer={null}
        centered
        styles={{ body: { padding: "16px 24px" } }}
      >
        <div ref={scrollRef} style={{ maxHeight: 680, overflow: "auto" }}>
          {/* ── ข้อมูลบิล ── */}
          <Descriptions
            size="small"
            bordered
            column={3}
            style={{ marginBottom: 16 }}
          >
            <Descriptions.Item label="เลขที่บิล">
              <Text strong>{dataLoad?.TEMPBILL}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="วันที่">
              {dataLoad?.items?.[0]?.dateRound
                ? dayjs(dataLoad.items[0].dateRound).format("DD/MM/YYYY")
                : "-"}
            </Descriptions.Item>
            <Descriptions.Item label="จำนวนสัญญา">
              <Text strong>
                {new Set(dataLoad?.items?.map((i) => i.CONTNO)).size} สัญญา
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label="สถานะอนุมัติ">
              <Tag
                color={
                  dataLoad?.items?.[0]?.APPROVE === 1 ? "success" : "warning"
                }
              >
                {dataLoad?.items?.[0]?.APPROVE === 1
                  ? "อนุมัติแล้ว"
                  : "รออนุมัติ"}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="สถานะบัญชี">
              <Tag
                color={
                  dataLoad?.items?.[0]?.APPROVE_ACC === 1
                    ? "success"
                    : "default"
                }
              >
                {dataLoad?.items?.[0]?.APPROVE_ACC === 1
                  ? "ผ่านบัญชี"
                  : "รอบัญชี"}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="ผู้อนุมัติ">
              {dataLoad?.items?.[0]?.USER_APPROVE ?? "-"}
            </Descriptions.Item>
          </Descriptions>

          {/* ── ตารางหลัก + expand ── */}
          <Table
            size="middle"
            rowKey={(rows) => rows[0]?.invId}
            columns={mainColumns}
            dataSource={groupedByInv}
            pagination={false}
            scroll={{ x: 900 }}
            expandable={{
              expandedRowRender: (rows) => (
                <Table
                  size="small"
                  columns={personColumns}
                  dataSource={rows}
                  rowKey="moneyOnlineId"
                  pagination={false}
                  bordered
                  style={{ margin: "8px 0" }}
                />
              ),
              defaultExpandAllRows: false,
            }}
            footer={() => {
              const withheld = operationTotal * 0.03;
              const netPay = grandTotal - withheld; // ✅ แก้จาก + เป็น - (grandTotal ลบ withheld)
              const approved = dataLoad?.items?.[0]?.APPROVE_ACC === 1;

              return (
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 10 }}
                >
                  {/* ── บรรทัด 1: ยอดรวมแต่ละคอลัมน์ ───────────────────── */}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      alignItems: "center",
                      gap: 8,
                      paddingBottom: 10,
                      borderBottom: "1px dashed var(--color-border-tertiary)",
                    }}
                  >
                    {/* จำนวนคนรวม */}
                    <div
                      style={{
                        background: "var(--color-background-secondary)",
                        borderRadius: 8,
                        padding: "8px 18px",
                        textAlign: "right",
                        minWidth: 110,
                      }}
                    >
                      <div
                        style={{
                          fontSize: 11,
                          color: "var(--color-text-secondary)",
                          marginBottom: 3,
                        }}
                      >
                        จำนวนคนรวม
                      </div>
                      <Text strong style={{ fontSize: 16 }}>
                        {editItems?.length} คน
                      </Text>
                    </div>

                    {/* ค่าคัดเอกสารรวม */}
                    <div
                      style={{
                        background: "var(--color-background-secondary)",
                        borderRadius: 8,

                        textAlign: "right",
                        minWidth: 130,
                      }}
                    >
                      <div
                        style={{
                          fontSize: 11,
                          color: "var(--color-text-secondary)",
                          marginBottom: 3,
                        }}
                      >
                        ค่าคัดเอกสารรวม
                      </div>
                      <Text strong style={{ fontSize: 16 }}>
                        {fmt(ducumentTotal)} ฿
                      </Text>
                    </div>

                    {/* ค่าดำเนินการรวม */}
                    <div
                      style={{
                        background: "var(--color-background-secondary)",
                        borderRadius: 8,

                        textAlign: "right",
                        minWidth: 130,
                      }}
                    >
                      <div
                        style={{
                          fontSize: 11,
                          color: "var(--color-text-secondary)",
                          marginBottom: 3,
                        }}
                      >
                        ค่าดำเนินการรวม
                      </div>
                      <Text strong style={{ fontSize: 16 }}>
                        {fmt(operationTotal)} ฿
                      </Text>
                    </div>

                    {/* ยอดรวม */}
                    <div
                      style={{
                        background: "var(--color-background-secondary)",
                        border: "0.5px solid var(--color-border-primary)",
                        borderRadius: 8,

                        textAlign: "right",
                        minWidth: 130,
                      }}
                    >
                      <div
                        style={{
                          fontSize: 11,
                          color: "var(--color-text-secondary)",
                          marginBottom: 3,
                        }}
                      >
                        ยอดรวม
                      </div>
                      <Text type="danger" strong style={{ fontSize: 16 }}>
                        {fmt(grandTotal)} ฿
                      </Text>
                    </div>
                  </div>

                  {/* ── บรรทัด 2: สถานะ + ยอดคำนวณสุดท้าย ──────────────── */}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 16,
                    }}
                  >
                    {/* สถานะ */}
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {approved
                        ? "✅ บิลถูกตั้งแล้ว"
                        : changedCount > 0
                          ? `⚠️ มีการแก้ไข ${changedCount} รายการ — กรุณากด "บันทึก" ก่อนตั้งบิล`
                          : "✅ ไม่มีการแก้ไข — พร้อมตั้งบิล"}
                    </Text>

                    {/* ยอดเงิน */}
                    <div
                      style={{ display: "flex", gap: 8, alignItems: "center" }}
                    >
                      <div
                        style={{
                          background: "var(--color-background-secondary)",
                          borderRadius: 8,
                          padding: "8px 18px",
                          textAlign: "right",
                          minWidth: 130,
                        }}
                      >
                        <div
                          style={{
                            fontSize: 11,
                            color: "var(--color-text-secondary)",
                            marginBottom: 3,
                          }}
                        >
                          ยอดรวมทั้งบิล
                        </div>
                        <Text type="danger" strong style={{ fontSize: 17 }}>
                          {fmt(grandTotal)} ฿
                        </Text>
                      </div>

                      <Text type="secondary">−</Text>

                      <div
                        style={{
                          background: "var(--color-background-secondary)",
                          borderRadius: 8,
                          padding: "8px 18px",
                          textAlign: "right",
                          minWidth: 130,
                        }}
                      >
                        <div
                          style={{
                            fontSize: 11,
                            color: "var(--color-text-secondary)",
                            marginBottom: 3,
                          }}
                        >
                          หัก ณ ที่จ่าย 3%
                        </div>
                        <Text type="warning" strong style={{ fontSize: 17 }}>
                          {fmt(withheld)} ฿
                        </Text>
                      </div>

                      <Text type="secondary">=</Text>

                      <div
                        style={{
                          background: "var(--color-background-success)",
                          border: "0.5px solid var(--color-border-success)",
                          borderRadius: 8,
                          padding: "8px 18px",
                          textAlign: "right",
                          minWidth: 130,
                        }}
                      >
                        <div
                          style={{
                            fontSize: 11,
                            color: "var(--color-text-success)",
                            marginBottom: 3,
                          }}
                        >
                          ยอดรวมจ่าย
                        </div>
                        <Text
                          style={{
                            fontSize: 17,
                            fontWeight: 500,
                            color: "green",
                          }}
                        >
                          {fmt(netPay)} ฿
                        </Text>
                      </div>
                    </div>
                  </div>
                </div>
              );
            }}
          />

          <Divider />

          {/* ── ปุ่มด้านล่าง ── */}
          <div style={{ textAlign: "center", paddingBottom: 8 }}>
            <Space size={16}>
              <Button
                size="large"
                danger
                icon={<CloseCircleOutlined />}
                onClick={handleCancel}
                disabled={isBusy}
              >
                ปิด
              </Button>
              {dataLoad?.items?.[0]?.APPROVE_ACC !== 1 && (
                <>
                  {/* บันทึก (active เฉพาะมีการแก้ไข) */}
                  <Button
                    size="large"
                    icon={<SaveOutlined />}
                    onClick={handleSave}
                    loading={loadingSave}
                    disabled={changedCount === 0 || loading}
                    style={{
                      borderColor: changedCount > 0 ? "#faad14" : undefined,
                      color: changedCount > 0 ? "#faad14" : undefined,
                    }}
                  >
                    บันทึกแก้ไข {changedCount > 0 && `(${changedCount})`}
                  </Button>

                  {/* ตั้งบิลเบิก */}
                  <Button
                    size="large"
                    type="primary"
                    icon={<CheckCircleOutlined />}
                    onClick={preDataApprove}
                    loading={loading}
                    disabled={changedCount > 0 || loadingSave} // บังคับบันทึกก่อน
                  >
                    ตั้งบิลเบิก
                  </Button>
                </>
              )}
            </Space>

            {/* hint เมื่อมีการแก้ไขค้างอยู่ */}
            {changedCount > 0 && (
              <div style={{ marginTop: 8 }}>
                <Text type="warning" style={{ fontSize: 12 }}>
                  ⚠️ กรุณากด "บันทึก" ก่อนจึงจะตั้งบิลได้
                </Text>
              </div>
            )}
          </div>
        </div>

        <FloatButton.BackTop
          target={() => scrollRef.current}
          visibilityHeight={80}
          style={{ right: 24, bottom: 24 }}
        />
      </Modal>
    </Spin>
  );
};

export default ListDataPayment;
